/**
 * Keeps a scrolling message list on its newest (bottom) item while the list's
 * own height changes — the composer under it growing as someone types, chips or
 * a link preview appearing — unless the user has scrolled up to read history.
 *
 * Only a scroll the USER makes (wheel, touch, scrollbar drag, keys) can
 * un-pin. A list getting shorter keeps its scrollTop, and WebKit additionally
 * nudges scrollTop back up by itself after a resize; judging "scrolled up" by
 * distance or direction alone would un-pin on those. Reaching the bottom again
 * re-pins.
 */
import { useEffect, useRef, type MutableRefObject, type RefObject } from 'react';

const AT_BOTTOM_PX = 24;
/** A scroll within this long after a wheel / touch / pointer / key on the list is the user's. */
const USER_SCROLL_WINDOW_MS = 1000;

export function useStickToBottom(
  listRef: RefObject<HTMLElement | null>,
  active = true,
  stickRef?: MutableRefObject<boolean>,
): MutableRefObject<boolean> {
  const ownRef = useRef(true);
  const stick = stickRef ?? ownRef;

  useEffect(() => {
    const el = listRef.current;
    if (!active || !el || typeof ResizeObserver === 'undefined') return;
    let userAt = 0;
    let lastTop = el.scrollTop;
    const markUser = () => {
      userAt = Date.now();
    };
    // Pin now and again on the next frame. WebKit can hand back the pre-resize
    // position for a few frames after a programmatic scroll; each of those
    // arrives as an upward, non-user scroll and is pinned again below.
    let frame = 0;
    const pin = () => {
      el.scrollTop = el.scrollHeight;
      lastTop = el.scrollTop;
      if (typeof requestAnimationFrame !== 'function') return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!stick.current) return;
        el.scrollTop = el.scrollHeight;
        lastTop = el.scrollTop;
      });
    };
    const onScroll = () => {
      const top = el.scrollTop;
      const movedUp = top < lastTop - 1;
      lastTop = top;
      if (el.scrollHeight - top - el.clientHeight < AT_BOTTOM_PX) stick.current = true;
      else if (Date.now() - userAt < USER_SCROLL_WINDOW_MS) stick.current = false;
      // The browser moved it up, not the user: back to the newest. (A smooth
      // scroll-to-bottom moving down is left to finish.)
      else if (stick.current && movedUp) pin();
    };
    const observer = new ResizeObserver(() => {
      if (stick.current) pin();
    });
    const userEvents = ['wheel', 'touchmove', 'pointerdown', 'keydown'] as const;
    el.addEventListener('scroll', onScroll, { passive: true });
    for (const t of userEvents) el.addEventListener(t, markUser, { passive: true });
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      for (const t of userEvents) el.removeEventListener(t, markUser);
      observer.disconnect();
      if (frame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
    };
  }, [listRef, active, stick]);

  return stick;
}
